/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react';
import { noop } from '@deltasierra/utilities-object';
import { useChange, useIsKeyPressed } from '@deltasierra/react-hooks';
import classNames from 'classnames';
import styled from 'styled-components';
import { Translate } from '../Translate';
import { PuppetTextInput } from '../Input';
import { Loader } from '../Generic';
import { Icon } from '../Icon';
import { CustomDropdownProps, CustomDropdown, DropdownListItem } from './CustomDropdown';

export type WithoutClearable = {
    clearable?: false | undefined;
};

export type WithClearable = {
    clearable: true;
    onClear?: () => void;
};

export type DropdownProps<T> = Partial<
    Pick<CustomDropdownProps, 'menuLoading' | 'menuStyle' | 'onOpenChange' | 'render' | 'rightAlign' | 'style'>
> & {
    disabled?: boolean;
    filterable?: boolean;
    getOptionTemplate: (item: T) => React.ReactElement;
    getSelectedTemplate?: (item: T) => React.ReactElement;
    getValue: (item: T) => string;
    isDisabled?: (item: T) => boolean;
    items: T[];
    loading?: boolean;
    onFilterChange?: (value: string) => void;
    onSelect: (item: T) => void;
    selectText?: string;
    value: T | null;
} & (WithClearable | WithoutClearable);

export function Dropdown<T>({
    filterable = false,
    getOptionTemplate,
    getSelectedTemplate = getOptionTemplate,
    getValue,
    isDisabled = () => false,
    items,
    loading = false,
    onFilterChange = noop,
    onSelect,
    render,
    selectText,
    value,
    ...allProps
}: DropdownProps<T>): React.ReactElement<DropdownProps<T>> {
    const menuProps = { filterable, getOptionTemplate, getValue, isDisabled, items, onFilterChange };

    let rest;

    let onClear: (() => void) | undefined;
    if (allProps.clearable) {
        const { onClear: _onClear, ...otherOptions } = allProps;
        rest = otherOptions;
        onClear = _onClear;
    } else {
        rest = allProps;
    }

    return (
        <Loader loading={loading} size="small">
            <CustomDropdown
                {...rest}
                render={
                    render
                        ? render
                        : ({ disabled, requestToggle }) => (
                              <DefaultDropdownButton
                                  clearable={allProps.clearable}
                                  disabled={disabled}
                                  selectText={selectText}
                                  template={value ? getSelectedTemplate(value) : undefined}
                                  toggle={() => requestToggle()}
                                  onClear={onClear}
                              />
                          )
                }
            >
                {({ requestClose }) => (
                    <DefaultDropdownMenu
                        onSelect={item => {
                            onSelect(item);
                            requestClose();
                        }}
                        {...menuProps}
                    />
                )}
            </CustomDropdown>
        </Loader>
    );
}
Dropdown.displayName = 'Dropdown';

type DropdownMenuProps<T> = Pick<
    DropdownProps<T>,
    'filterable' | 'getOptionTemplate' | 'getValue' | 'isDisabled' | 'items' | 'onFilterChange' | 'onSelect'
>;

export function DefaultDropdownMenu<T>({
    filterable,
    getOptionTemplate,
    getValue,
    isDisabled = () => false,
    items,
    onFilterChange = noop,
    onSelect,
}: DropdownMenuProps<T>): React.ReactElement<DropdownMenuProps<T>> {
    const [filterValue, setFilterValue] = useChange<string>('', onFilterChange);
    const [focusedIndex, setFocusedIndex] = React.useState(-1);

    React.useEffect(() => {
        setFocusedIndex(0);
    }, [filterValue]);

    const handleClickOption = React.useCallback(
        (item: T) => {
            if (!isDisabled(item)) {
                onSelect(item);
            }
        },
        [isDisabled, onSelect],
    );

    const downPressed = useIsKeyPressed('ArrowDown', true);
    const upPressed = useIsKeyPressed('ArrowUp', true);
    const enterPressed = useIsKeyPressed('Enter', true);

    React.useEffect(() => {
        if (downPressed) {
            setFocusedIndex(prev => Math.min(prev + 1, items.length - 1));
        }
    }, [downPressed, items.length]);

    React.useEffect(() => {
        if (upPressed) {
            setFocusedIndex(prev => Math.max(prev - 1, 0));
        }
    }, [upPressed, items.length]);

    React.useEffect(() => {
        if (enterPressed && !!items[focusedIndex]) {
            handleClickOption(items[focusedIndex]);
        }
    }, [enterPressed, items, focusedIndex, handleClickOption]);

    return (
        <>
            {filterable && (
                <li style={{ padding: '0 0.5rem 0.5rem' }}>
                    <PuppetTextInput
                        fullWidth
                        ref={node => {
                            if (node) {
                                node.focus();
                            }
                        }}
                        value={filterValue}
                        onChange={event => setFilterValue(event.target.value)}
                    />
                </li>
            )}
            {filterable && items.length === 0 && (
                <DropdownListItem className="disabled" onClick={noop}>
                    <Translate keyId="COMMON.NO_RESULTS" />
                </DropdownListItem>
            )}
            {items.map((item, index) => (
                <DropdownListItem
                    className={classNames({ disabled: isDisabled(item) })}
                    focused={index === focusedIndex}
                    key={getValue(item)}
                    onClick={() => handleClickOption(item)}
                >
                    {getOptionTemplate(item)}
                </DropdownListItem>
            ))}
        </>
    );
}
DefaultDropdownMenu.displayName = 'DefaultDropdownMenu';

export type DefaultDropdownButtonProps = {
    disabled?: boolean;
    selectText?: string;
    template?: React.ReactElement;
    toggle: React.MouseEventHandler;
} & (WithClearable | WithoutClearable);

const StyledSpan = styled.span`
    position: absolute;
    top: 0;
    right: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
    pointer-events: auto;

    min-width: 30px;
    height: 100%;

    z-index: 2;
`;

const SpanWithEllipsis = styled.span`
    max-width: 100%;
    text-overflow: ellipsis;
    overflow-x: hidden;
`;

export const DefaultDropdownButton: React.FC<DefaultDropdownButtonProps> = ({
    disabled,
    selectText,
    template,
    toggle,
    ...otherProps
}) => (
    <button
        className={classNames('btn', 'btn-default', 'dropdown-toggle', 'btn-block', { disabled })}
        type="button"
        onClick={toggle}
    >
        <SpanWithEllipsis className="pull-left title">
            {template ?? selectText ?? <Translate keyId="COMMON.SELECT_AN_OPTION" />}
        </SpanWithEllipsis>
        {otherProps.clearable && template !== undefined ? (
            <StyledSpan
                onClick={event => {
                    event.stopPropagation();
                    otherProps.onClear?.();
                }}
            >
                <Icon icon="remove" />
            </StyledSpan>
        ) : (
            <span className="caret caret-right-align" />
        )}
    </button>
);
DefaultDropdownButton.displayName = 'DefaultDropdownButton';
